import {
  calculateDurationMinutes,
  formatDuration,
  formatTime,
  getUserInitials,
  isBlockedEvent,
  getEventTypeConfig,
  getStatusConfig,
  formatDate,
  getEventTypeLabel,
  Icons,
  PARTICIPANT_STATUSES,
} from "../../utils";
import DomainBadge from "../common/DomainBadge.jsx";
import { useAppMode } from "../../context/AppMode.jsx";

/* ─── SlotCard ─────────────────────────────────────────────────── */
export default function SlotCard({
  slot,
  compact = false,
  onCardClick,
  onRegisterClick,
  onDeleteClick,
}) {
  const { mode, currentUser } = useAppMode();
  const { slot_date, start_time, end_time, is_all_day, slot_status } = slot;
  const event = slot.event || {};

  const blocked = isBlockedEvent(event);

  // Participants (non annulés)
  const participants = (slot.participants || []).filter(
    (p) => p.status !== PARTICIPANT_STATUSES.CANCELLED,
  );
  const participantCount = participants.length;

  // Instructors
  const instructors = slot.instructors || [];

  // Label horaire
  const duration = is_all_day
    ? null
    : calculateDurationMinutes(start_time, end_time);
  const timeLabel = is_all_day
    ? "Toute la journée"
    : `${formatTime(start_time)} · ${formatDuration(duration)}`;

  const handleCardClick = () => {
    if (onCardClick) {
      onCardClick(slot);
    }
  };

  const handleRegisterClick = (e) => {
    e.stopPropagation(); // Empêche la propagation au clic de la card
    if (onRegisterClick) {
      onRegisterClick(slot);
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation(); // Empêche la propagation au clic de la card
    if (onDeleteClick) {
      onDeleteClick(slot);
    }
  };

  return (
    <div
      className={`slot-card slot-${event.event_type} ${compact ? "compact" : ""}`}
      onClick={handleCardClick}
      style={{ cursor: onCardClick ? "pointer" : "default" }}
    >
      {/* En-tête */}
      <div className="slot-card__header">
        <div className="slot-card__title">
          <div className={`slot-card__name ${compact ? "compact" : ""}`}>
            {event.name || getEventTypeLabel(event.event_type)}{" "}
            <DomainBadge config={getEventTypeConfig(event.event_type)} />
          </div>
          <div className="slot-card__time">{timeLabel}</div>
        </div>

        {!blocked && currentUser && (
          <button className="slot-card__cta" onClick={handleRegisterClick}>
            S'inscrire
          </button>
        )}
      </div>

      {/* Enseignants */}
      {instructors.length > 0 && (
        <div className="slot-card__instructors">
          Avec{" "}
          {instructors
            .map((inst) => {
              const u = inst.users;
              return u ? `${u.first_name}` : null;
            })
            .filter(Boolean)
            .join(", ")}
        </div>
      )}

      {/* Participants */}
      <div className="slot-card__participants">
        {participantCount > 0 && (
          <div className="slot-card__avatars">
            {participants.slice(0, 3).map((p, i) => (
              <div
                key={p.id}
                className="slot-card__avatar"
                style={{ zIndex: 3 - i }}
                title={
                  p.users
                    ? `${p.users.first_name} ${p.users.last_name}`
                    : "Inconnu"
                }
              >
                {p.users ? getUserInitials(p.users) : "?"}
              </div>
            ))}

            {participantCount > 3 && (
              <div className="slot-card__avatar slot-card__avatar--more">
                +{participantCount - 3}
              </div>
            )}
          </div>
        )}

        <span className="slot-card__count">
          {participantCount} participant{participantCount !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Infos du créneau */}
      <div className="slot-card__info">
        <div>Date : {formatDate(slot_date)}</div>
        {!is_all_day ? (
          <div>
            Heure : {start_time} – {end_time}
          </div>
        ) : (
          "Journée complète"
        )}
        <div className="slot-card__status">
          <DomainBadge config={getStatusConfig(slot_status)} />
        </div>
      </div>

      {/* Bouton de suppression */}
      {onDeleteClick && (
        <button
          className="slot-card__delete-btn"
          onClick={handleDeleteClick}
          title="Supprimer ce créneau"
          aria-label="Supprimer ce créneau"
        >
          <Icons.Delete />
        </button>
      )}
    </div>
  );
}
